import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { useIsMobile } from '@/hooks/layout/use-mobile';
import { cn } from '@/lib/utils';

const AppCardSkeleton: React.FC = () => {
  const isMobile = useIsMobile();
  
  // Match the same card classes as AppCard - INCLUDING h-full flex flex-col
  const cardClasses = cn(
    "group relative rounded-lg overflow-hidden cursor-pointer h-full flex flex-col",
    "transition-all duration-300 ease-in-out",
    "shadow-sm hover:shadow-md hover:shadow-amber-100/30 dark:hover:shadow-amber-900/20"
  );

  return (
    <div 
      className={cardClasses}
      style={{ 
        borderRadius: '0.5rem',
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
        willChange: 'transform, box-shadow',
        transformStyle: 'preserve-3d',
        WebkitFontSmoothing: 'antialiased'
      }}
    >
      {/* Background gradient to match AppCard */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-white/40 via-white/20 to-white/5 dark:from-black/60 dark:to-black/30 backdrop-blur-xl backdrop-saturate-150 -z-10 rounded-lg will-change-transform" 
        style={{ transform: 'translateZ(0)' }}
      />
      
      {/* Content - matching AppCard padding AND flex structure */}
      <div className={`${isMobile ? 'p-3 pb-4' : 'p-5 pb-6'} rounded-lg flex flex-col h-full`} style={{ willChange: 'contents' }}>
        <div className="flex items-start justify-between mb-2 md:mb-3">
          <div className="flex items-center gap-2 md:gap-3">
            {/* Icon container skeleton */}
            <div className={cn(
              "p-2 md:p-3 rounded-lg",
              "bg-gradient-to-br from-slate-100/50 to-slate-50/50 dark:from-slate-800/50 dark:to-slate-900/50",
              "flex items-center justify-center"
            )}>
              <Skeleton className="w-5 h-5 md:w-7 md:h-7 rounded" />
            </div>
            <div className="flex flex-row items-center gap-2">
              {/* Title skeleton */}
              <Skeleton className="h-4 md:h-5 w-24 md:w-32" />
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="ml-auto"></div>
          </div>
        </div>

        {/* Description skeleton - MUST have flex-1 to match AppCard */}
        <div className="flex-1 mb-2 md:mb-4">
          <Skeleton className="h-3 md:h-4 w-full mb-2" />
          <Skeleton className="h-3 md:h-4 w-3/4" />
        </div>
        
        {/* Bottom section with favorites, pin badge, and Coming Soon tag on left, category on right - MUST have mt-auto */}
        <div className="flex items-end justify-between mt-auto">
          <div className="flex items-center gap-2 mt-2">
            {/* Favorite button skeleton */}
            <Skeleton className="h-6 w-6 rounded-full" />
            {/* Pin badge skeleton */}
            <Skeleton className="h-5 w-12 rounded-full" />
            {/* Coming Soon tag skeleton */}
            <Skeleton className="h-5 w-24 rounded-full" />
          </div>
          
          {/* Category tag skeleton on the right side */}
          <div className="flex items-center gap-2 mt-2">
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppCardSkeleton;
